import {createSlice, type PayloadAction, type Reducer} from '@reduxjs/toolkit';
import {captureException} from '@sentry/browser';

import type {SliceActions} from 'redux-stores/Action';
import type {BenchlingState} from 'redux-stores/reduceApp';
import type {Thunk} from 'redux-stores/Store';
import * as Net from 'util/Net';

import {claimRealtimeVoiceStart} from './RealtimeVoiceChatHandoff';

export interface VoiceSessionControlOwner {
  ownerId: string;
  ownerInstanceId: string;
  chatSessionId: string | null;
}

export interface VoiceSessionControlOwnerState {
  ownerInstanceId: string;
  chatSessionId: string | null;
  requestId: string | null;
}

export interface VoiceSessionControlState {
  owners: Record<string, VoiceSessionControlOwnerState | undefined>;
}

const initialState: VoiceSessionControlState = {owners: {}};

interface VoiceStartIdentity extends VoiceSessionControlOwner {
  requestId: string;
}

function getMatchingOwner(
  state: VoiceSessionControlState,
  identity: VoiceSessionControlOwner
): VoiceSessionControlOwnerState | undefined {
  const owner = state.owners[identity.ownerId];
  return owner?.ownerInstanceId === identity.ownerInstanceId && owner.chatSessionId === identity.chatSessionId
    ? owner
    : undefined;
}

function getMatchingRequest(
  state: VoiceSessionControlState,
  identity: VoiceStartIdentity
): VoiceSessionControlOwnerState | undefined {
  const owner = getMatchingOwner(state, identity);
  return owner?.requestId === identity.requestId ? owner : undefined;
}

/**
 * Manages Voice startup requests. React components own the audio connection
 * and callbacks; this slice records which owner and request may accept an asynchronous result.
 */
export const voiceSessionControl = createSlice({
  name: 'voiceSessionControl',
  initialState,
  reducers: {
    ownerRegistered: (state, {payload}: PayloadAction<VoiceSessionControlOwner>) => {
      state.owners[payload.ownerId] = {
        ownerInstanceId: payload.ownerInstanceId,
        chatSessionId: payload.chatSessionId,
        requestId: null,
      };
    },
    ownerReleased: (state, {payload}: PayloadAction<VoiceSessionControlOwner>) => {
      if (getMatchingOwner(state, payload) != null) {
        delete state.owners[payload.ownerId];
      }
    },
    startRequested: (state, {payload}: PayloadAction<VoiceStartIdentity>) => {
      const owner = getMatchingOwner(state, payload);
      if (owner == null) {
        return;
      }
      owner.requestId = payload.requestId;
    },
    sessionStartSucceeded: (state, {payload}: PayloadAction<VoiceStartIdentity>) => {
      const owner = getMatchingRequest(state, payload);
      if (owner == null) {
        return;
      }
      owner.requestId = null;
    },
    sessionStartFailed: (state, {payload}: PayloadAction<VoiceStartIdentity>) => {
      const owner = getMatchingRequest(state, payload);
      if (owner == null) {
        return;
      }
      owner.requestId = null;
    },
    startCancelled: (state, {payload}: PayloadAction<VoiceSessionControlOwner>) => {
      const owner = getMatchingOwner(state, payload);
      if (owner?.requestId == null) {
        return;
      }
      owner.requestId = null;
    },
  },
});

export const voiceSessionControlActions = voiceSessionControl.actions;
export type VoiceSessionControlAction = SliceActions<typeof voiceSessionControl.actions>;
export const reduceVoiceSessionControl = voiceSessionControl.reducer as Reducer<
  VoiceSessionControlState,
  VoiceSessionControlAction
>;

let nextVoiceSessionControlId = 0;

export function createVoiceSessionControlId(kind: 'owner' | 'instance' | 'request'): string {
  nextVoiceSessionControlId += 1;
  return `voice-${kind}-${nextVoiceSessionControlId}`;
}

export function selectVoiceSessionControlOwner(
  state: BenchlingState,
  owner: VoiceSessionControlOwner
): VoiceSessionControlOwnerState | undefined {
  return getMatchingOwner(state.ui.voiceSessionControl, owner);
}

function selectIsVoiceStartCurrent(state: BenchlingState, identity: VoiceStartIdentity): boolean {
  return getMatchingRequest(state.ui.voiceSessionControl, identity) != null;
}

interface RequestVoiceStartOptions extends VoiceSessionControlOwner {
  writeActionsEnabled: boolean | undefined;
  // Allows supporting academic accounts without approvals enabled by skipping
  // the Full actions permission request. This mode is not currently used.
  attemptAutomaticGrant: boolean;
  reason?: 'user' | 'new-chat-handoff';
  grantFullAccess?: () => Promise<boolean>;
  startVoiceSession?: () => Promise<void>;
}

/**
 * Starts Voice, optionally enabling Full actions first. A denied grant or server-request error
 * still allows a Default-mode start; an unexpected grant error stops startup and is rethrown.
 *
 * A new-chat handoff waits for known permissions, claims startup once, and skips the grant request
 * because chat creation already attempted it.
 *
 * Redux tracks progress; the caller's callbacks request the grant and start the audio session.
 * If the owning component unmounts, the chat changes, or the request is cancelled or replaced,
 * ignore its results and do not advance startup. These checks do not undo work already in flight.
 */
export function requestVoiceStart({
  writeActionsEnabled,
  attemptAutomaticGrant,
  reason = 'user',
  grantFullAccess,
  startVoiceSession,
  ...owner
}: RequestVoiceStartOptions): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    if (
      writeActionsEnabled == null ||
      startVoiceSession == null ||
      selectVoiceSessionControlOwner(getState(), owner) == null
    ) {
      return;
    }
    // Ignore duplicate starts while this owner's request is in flight.
    if (selectVoiceSessionControlOwner(getState(), owner)?.requestId != null) {
      return;
    }
    const isHandoff = reason === 'new-chat-handoff';
    if (isHandoff && !claimRealtimeVoiceStart(owner.chatSessionId)) {
      return;
    }

    // 1. Register this attempt. Only the current request for this component and chat may proceed.
    const requestIdentity = {...owner, requestId: createVoiceSessionControlId('request')};
    dispatch(voiceSessionControlActions.startRequested(requestIdentity));
    if (!selectIsVoiceStartCurrent(getState(), requestIdentity)) {
      return;
    }

    // 2. Try to enable Full actions; Voice can still start in Default mode if denied.
    if (!isHandoff && attemptAutomaticGrant && !writeActionsEnabled && grantFullAccess != null) {
      try {
        await grantFullAccess();
      } catch (error) {
        if (!selectIsVoiceStartCurrent(getState(), requestIdentity)) {
          return;
        }
        if (!Net.isServerRequestError(error)) {
          dispatch(voiceSessionControlActions.sessionStartFailed(requestIdentity));
          throw error;
        }
        // The Full actions permission request failed; Voice can still start in Default mode if authorized.
        captureException(error);
      }
      // The user may have left this chat or cancelled startup while the grant request was pending.
      if (!selectIsVoiceStartCurrent(getState(), requestIdentity)) {
        return;
      }
    }

    // 3. Start Voice with the available permissions, whether or not Full actions permission was granted.
    if (!selectIsVoiceStartCurrent(getState(), requestIdentity)) {
      return;
    }
    try {
      await startVoiceSession();
      // 4. Record the outcome only if this attempt is still current; apply the same rule to failures.
      if (!selectIsVoiceStartCurrent(getState(), requestIdentity)) {
        return;
      }
      dispatch(voiceSessionControlActions.sessionStartSucceeded(requestIdentity));
    } catch (error) {
      if (!selectIsVoiceStartCurrent(getState(), requestIdentity)) {
        return;
      }
      dispatch(voiceSessionControlActions.sessionStartFailed(requestIdentity));
      throw error;
    }
  };
}

/** Cancel startup before stopping audio, so a late grant cannot start the session again. */
export function requestVoiceStop(
  owner: VoiceSessionControlOwner,
  stopVoiceSession: (() => Promise<void>) | undefined
): Thunk<Promise<void>> {
  return async (dispatch) => {
    dispatch(voiceSessionControlActions.startCancelled(owner));
    await stopVoiceSession?.();
  };
}
